import { useState, useEffect } from 'react'
import { useAuth } from '@/auth/AuthContext'

const API = import.meta.env.VITE_API_URL || 'http://localhost:5001/api'

// ─── Constants ────────────────────────────────────────────────────────────────
const DOMAINS = [
  { id: 'physical-motor',    label: 'Physical & Motor',    short: 'Physical',  icon: '🏃', color: '#1B8A5A', bg: '#EDF7F2', border: '#52BE80' },
  { id: 'socio-emotional',   label: 'Socio-Emotional',     short: 'Socio',     icon: '💛', color: '#C0392B', bg: '#FDEDEC', border: '#E57373' },
  { id: 'cognitive',         label: 'Cognitive',           short: 'Cognitive', icon: '🧠', color: '#7B2D8B', bg: '#F5EEF8', border: '#A569BD' },
  { id: 'language-literacy', label: 'Language & Literacy', short: 'Language',  icon: '📚', color: '#003366', bg: '#E8F0FE', border: '#4A90D9' },
  { id: 'numeracy',          label: 'Early Numeracy',      short: 'Numeracy',  icon: '🔢', color: '#F57F17', bg: '#FFFDE7', border: '#FFD54F' },
]

const ALL_INDICATORS = [
  {id:'pm1',domainId:'physical-motor',label:'Holds pencil/crayon with control'},{id:'pm2',domainId:'physical-motor',label:'Cuts along a line with scissors'},
  {id:'pm3',domainId:'physical-motor',label:'Strings beads or laces shoes'},{id:'pm4',domainId:'physical-motor',label:'Draws basic shapes (circle, square)'},
  {id:'pm5',domainId:'physical-motor',label:'Walks, runs, jumps with coordination'},{id:'pm6',domainId:'physical-motor',label:'Catches and throws a ball'},
  {id:'pm7',domainId:'physical-motor',label:'Balances on one foot briefly'},{id:'pm8',domainId:'physical-motor',label:'Climbs, crawls, rolls with confidence'},
  {id:'se1',domainId:'socio-emotional',label:'Identifies and names basic emotions'},{id:'se2',domainId:'socio-emotional',label:'Regulates strong emotions with support'},
  {id:'se3',domainId:'socio-emotional',label:'Shares and takes turns with peers'},{id:'se4',domainId:'socio-emotional',label:'Shows empathy toward others'},
  {id:'se5',domainId:'socio-emotional',label:'Separates from caregiver without distress'},{id:'se6',domainId:'socio-emotional',label:'Participates in group activities'},
  {id:'se7',domainId:'socio-emotional',label:'Follows simple classroom rules'},{id:'se8',domainId:'socio-emotional',label:'Expresses needs and wants clearly'},
  {id:'cg1',domainId:'cognitive',label:'Sorts objects by color, shape, size'},{id:'cg2',domainId:'cognitive',label:'Identifies patterns and continues them'},
  {id:'cg3',domainId:'cognitive',label:'Solves simple cause-and-effect problems'},{id:'cg4',domainId:'cognitive',label:'Matches and categorizes objects'},
  {id:'cg5',domainId:'cognitive',label:'Sequences events in correct order'},{id:'cg6',domainId:'cognitive',label:'Demonstrates memory for recent events'},
  {id:'cg7',domainId:'cognitive',label:'Completes simple puzzles (4–8 pieces)'},{id:'cg8',domainId:'cognitive',label:'Shows curiosity and asks questions'},
  {id:'ll1',domainId:'language-literacy',label:'Listens and responds to simple instructions'},{id:'ll2',domainId:'language-literacy',label:'Speaks in 3–5 word sentences'},
  {id:'ll3',domainId:'language-literacy',label:'Recognizes and names letters (mother tongue)'},{id:'ll4',domainId:'language-literacy',label:'Identifies beginning sounds of words'},
  {id:'ll5',domainId:'language-literacy',label:'Retells a story with key details'},{id:'ll6',domainId:'language-literacy',label:'Shows book-handling behaviour (left-right)'},
  {id:'ll7',domainId:'language-literacy',label:'Engages in conversation with turns'},{id:'ll8',domainId:'language-literacy',label:'Recognizes own name in print'},
  {id:'nm1',domainId:'numeracy',label:'Counts objects 1–10 with 1-to-1 correspondence'},{id:'nm2',domainId:'numeracy',label:'Recognizes numerals 1–10'},
  {id:'nm3',domainId:'numeracy',label:'Compares quantities (more, less, equal)'},{id:'nm4',domainId:'numeracy',label:'Identifies basic shapes in environment'},
  {id:'nm5',domainId:'numeracy',label:'Uses positional language (above, below, beside)'},{id:'nm6',domainId:'numeracy',label:'Completes simple AB/ABC patterns'},
  {id:'nm7',domainId:'numeracy',label:'Understands concept of addition (adding more)'},{id:'nm8',domainId:'numeracy',label:'Sorts and groups by number'},
]

const LEVELS = {
  'not-observed':{ short:'N/O', color:'#9E9E9E', bg:'#F5F5F5', score:0, label:'Not Observed' },
  'emerging':    { short:'E',   color:'#E53935', bg:'#FFEBEE', score:1, label:'Emerging'      },
  'developing':  { short:'D',   color:'#F9A825', bg:'#FFF9C4', score:2, label:'Developing'    },
  'achieved':    { short:'A',   color:'#2E7D32', bg:'#E8F5E9', score:3, label:'Achieved'      },
}

const HOME_TIPS = {
  'physical-motor':   ['🖍️ Practice drawing shapes and lines together','✂️ Cutting activities with safe child scissors','🏀 Catch-and-throw games to build coordination'],
  'socio-emotional':  ['🎭 Name emotions using picture books or cards','🧩 Play turn-taking board games as a family','🤝 Encourage sharing during unstructured play'],
  'cognitive':        ['🧩 Sort household objects by color, shape, or size','🔍 Simple puzzles and matching picture games','📖 Ask "what happens next?" while reading together'],
  'language-literacy':['📖 Read together for at least 15 minutes every day','🎵 Sing nursery rhymes and alphabet songs','💬 Talk about the day — "What was the best part?"'],
  'numeracy':         ['🔢 Count objects during daily routines (stairs, fruits)','🛒 Compare quantities while shopping together','🎲 Play simple number-based board or card games'],
}

const MODE_ICON = { flashcard:'🃏', worksheet:'📝', activitycard:'🖼️', storybook:'📖', workplan:'📅' }

// ─── WorkPlan helpers ─────────────────────────────────────────────────────────
function wpGetMonday(d) {
  const date = new Date(d), day = date.getDay()
  const diff = day === 0 ? 1 : (1 - day)
  date.setDate(date.getDate() + diff)
  return date
}
function wpFormatWeek(dateStr) {
  if(!dateStr) return '—'
  const d=new Date(dateStr), end=new Date(d); end.setDate(end.getDate()+5)
  return `${d.toLocaleDateString("en-IN",{day:"numeric",month:"short"})} – ${end.toLocaleDateString("en-IN",{day:"numeric",month:"short",year:"numeric"})}`
}
function wpTodayIdx() {
  const d=new Date().getDay(); return d===0?4:Math.min(d-1,5)
}
const WP_DAYS  = ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"]
const WP_SDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat"]
const WP_ACT_TYPES = [
  {id:"flashcard",icon:"🃏",color:"#1D4ED8",bg:"#EFF6FF"},{id:"worksheet",icon:"📝",color:"#7C3AED",bg:"#F5F3FF"},
  {id:"activity-card",icon:"🖼️",color:"#059669",bg:"#ECFDF5"},{id:"storybook",icon:"📖",color:"#B45309",bg:"#FFFBEB"},
  {id:"free-play",icon:"🎮",color:"#EA580C",bg:"#FFF7ED"},{id:"art-craft",icon:"🎨",color:"#BE185D",bg:"#FDF2F8"},
  {id:"music",icon:"🎵",color:"#1D4ED8",bg:"#EFF6FF"},{id:"outdoor",icon:"🌿",color:"#15803D",bg:"#F0FDF4"},
  {id:"assessment",icon:"📊",color:"#B45309",bg:"#FFFBEB"},{id:"custom",icon:"✨",color:"#6B7280",bg:"#F9FAFB"},
]
const BACKEND_DOM_MAP = {
  "language-literacy":"Language & Literacy","cognitive":"Cognitive Development",
  "socio-emotional":"Socio-Emotional","physical-motor":"Physical & Motor","numeracy":"Early Numeracy",
}
const BACKEND_MOD_MAP = {
  "flashcard":"flashcard","worksheet":"worksheet","activitycard":"activity-card","storybook":"storybook","custom":"custom",
}
function wpGetAType(id){ return WP_ACT_TYPES.find(t=>t.id===id)||WP_ACT_TYPES[WP_ACT_TYPES.length-1] }
function wpBackendToPlan(wp) {
  if(!wp) return null
  return {
    _id:wp._id, title:wp.title||"Weekly Work Plan",
    weekOf:wp.weekStart?new Date(wp.weekStart).toISOString().split("T")[0]:"",
    childLabel:wp.child?.name||"All Children", notes:wp.overallGoal||"",
    days:(wp.days||[]).map(d=>({
      day:d.label,
      activities:(d.entries||[]).map(e=>({
        id:String(e._id), type:BACKEND_MOD_MAP[e.moduleType]||"custom",
        title:e.contentTitle||e.goal||"Untitled", duration:`${e.durationMin||15} min`,
        notes:e.goal||"", domain:BACKEND_DOM_MAP[e.domain]||"Language & Literacy",
        completed:e.status==="done", _status:e.status, _sessionNote:e.sessionNote||"",
      })),
    })),
  }
}
function isInPlan(setTitle, plan) {
  if(!plan||!setTitle) return false
  const norm=s=>s.toLowerCase().replace(/[^a-z0-9]/g,"")
  const st=norm(setTitle)
  return plan.days.some(d=>d.activities.some(a=>{
    const at=norm(a.title)
    return at.includes(st)||st.includes(at)||at===st
  }))
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function flattenRecord(apiRecord) {
  if(!apiRecord?.indicators) return {}
  const out={}
  for(const ind of apiRecord.indicators){
    out[`${ind.domainId}::${ind.indicatorId}`]={level:ind.current?.level||'not-observed',note:ind.current?.note||''}
  }
  return out
}
function domainPct(record={},domainId) {
  const keys=Object.keys(record).filter(k=>k.startsWith(domainId+'::'))
  if(!keys.length) return 0
  return Math.round(keys.reduce((s,k)=>s+(LEVELS[record[k]?.level]?.score??0),0)/(keys.length*3)*100)
}
function overallPct(record={}) {
  const scores=DOMAINS.map(d=>domainPct(record,d.id))
  return Math.round(scores.reduce((a,b)=>a+b,0)/scores.length)
}
function calcAge(dob) {
  if(!dob) return null
  const d=new Date(dob),now=new Date(),y=now.getFullYear()-d.getFullYear()
  return now.getMonth()<d.getMonth()||(now.getMonth()===d.getMonth()&&now.getDate()<d.getDate())?y-1:y
}
function relDate(iso) {
  if(!iso) return '—'
  const d=Math.floor((Date.now()-new Date(iso).getTime())/86400000)
  if(d===0) return 'Today'; if(d===1) return 'Yesterday'; if(d<7) return `${d}d ago`; if(d<30) return `${Math.floor(d/7)}w ago`
  return new Date(iso).toLocaleDateString('en-IN',{day:'numeric',month:'short'})
}
function initials(name=''){return name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
function accColor(pct){if(pct==null)return '#9E9E9E';return pct>=70?'#1B6B45':pct>=40?'#D4AC0D':'#C0392B'}
function accBg(pct){if(pct==null)return '#F5F5F5';return pct>=70?'#F0FFF4':pct>=40?'#FFFDE7':'#FFEBEE'}
function sessAcc(s){return s?.ratedCount>0?Math.round((s.correctCount||0)/s.ratedCount*100):null}

// ─── Atoms ────────────────────────────────────────────────────────────────────
function Bar({pct,color,h=6}){
  return <div style={{height:h,background:'#ECF0F5',borderRadius:h,overflow:'hidden'}}><div style={{width:`${Math.min(100,pct)}%`,height:'100%',background:color,borderRadius:h,transition:'width .5s ease'}}/></div>
}
function Pill({label,color,bg,size=10}){
  return <span style={{display:'inline-block',background:bg||`${color}18`,color,fontSize:size,fontWeight:700,padding:'2px 8px',borderRadius:20,whiteSpace:'nowrap'}}>{label}</span>
}
function Avatar({name='',color='#1A4A6E',size=36,ring}){
  const circ=2*Math.PI*((size+8-4)/2)
  return (
    <div style={{position:'relative',width:size,height:size,flexShrink:0}}>
      {ring!=null&&(
        <svg width={size+8} height={size+8} style={{position:'absolute',top:-4,left:-4,transform:'rotate(-90deg)',pointerEvents:'none'}}>
          <circle cx={(size+8)/2} cy={(size+8)/2} r={(size+8-4)/2} fill="none" stroke="#E4ECF5" strokeWidth={3.5}/>
          <circle cx={(size+8)/2} cy={(size+8)/2} r={(size+8-4)/2} fill="none" stroke={color} strokeWidth={3.5}
            strokeDasharray={circ} strokeDashoffset={circ-ring/100*circ} strokeLinecap="round" style={{transition:'stroke-dashoffset .7s ease'}}/>
        </svg>
      )}
      <div style={{width:size,height:size,borderRadius:'50%',background:color,display:'flex',alignItems:'center',justifyContent:'center',color:'white',fontWeight:900,fontSize:size*0.34}}>{initials(name)}</div>
    </div>
  )
}
function Spinner({size=20,color='#1A4A6E'}){
  return <div style={{width:size,height:size,border:`3px solid ${color}22`,borderTop:`3px solid ${color}`,borderRadius:'50%',animation:'pd-spin .7s linear infinite'}}/>
}
function EmptyState({icon,text}){
  return <div style={{textAlign:'center',padding:'28px 0',color:'#B0BECC'}}><div style={{fontSize:28,marginBottom:8}}>{icon}</div><div style={{fontSize:12,fontWeight:600}}>{text}</div></div>
}

// ─── FlashcardsTab ────────────────────────────────────────────────────────────
function FlashcardsTab({ sets, child, onNav }) {
  // null = still loading, [] = loaded but empty, [...] = has sets
  if (sets === null) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',
                 padding:60,flexDirection:'column',gap:12}}>
      <Spinner size={24}/>
      <div style={{fontSize:13,color:'#9EB0C4'}}>Loading flashcard sets…</div>
    </div>
  )

  if (!sets || sets.length === 0) return (
    <div style={{background:'white',borderRadius:14,padding:'48px 24px',
                 textAlign:'center',border:'1.5px dashed #E4ECF5',color:'#9EB0C4'}}>
      <div style={{fontSize:40,marginBottom:12}}>🃏</div>
      <div style={{fontWeight:800,fontSize:15,marginBottom:6,color:'#1A2E4A'}}>
        No sets assigned yet
      </div>
      <div style={{fontSize:12,lineHeight:1.8,maxWidth:300,margin:'0 auto'}}>
        {child.name.split(' ')[0]}'s teacher hasn't assigned any flashcard sets yet.
        They'll appear here once assigned.
      </div>
    </div>
  )

  const CARD_TYPE_LABEL = {
    'image-text': 'Image + Text',
    'image-only': 'Image Only',
    'text-only':  'Text Only',
  }
  const LANG_NAME = { en:'English', ml:'Malayalam', hi:'Hindi', ta:'Tamil', te:'Telugu' }
  const LANG_FLAG = { en:'🇬🇧', ml:'🇮🇳', hi:'🇮🇳', ta:'🇮🇳', te:'🇮🇳' }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>

      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-end',justifyContent:'space-between'}}>
        <div>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:18,
                       fontWeight:900,color:'#1A2E4A'}}>
            Assigned Flashcard Sets
          </div>
          <div style={{fontSize:12,color:'#8FA5BC',marginTop:3}}>
            {sets.length} set{sets.length !== 1 ? 's' : ''} assigned to {child.name.split(' ')[0]} by their teacher
          </div>
        </div>
        <div style={{fontSize:11,color:'#8FA5BC',background:'#F4F7FB',
                     padding:'4px 10px',borderRadius:8,border:'1px solid #E4ECF5'}}>
          Flip mode only · Read-only
        </div>
      </div>

      {/* Set cards grid */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:16}}>
        {sets.map(set => {
          const cardCount = (set.cards || []).length
          // Up to 4 preview images (skip SVG placeholders)
          const preview = (set.cards || [])
            .filter(c => c.imageUrl && !c.imageUrl.startsWith('data:image/svg'))
            .slice(0, 4)

          return (
            <div key={set._id}
              style={{background:'white',borderRadius:16,border:'1.5px solid #E4ECF5',
                      overflow:'hidden',boxShadow:'0 2px 10px rgba(0,0,0,.05)',
                      display:'flex',flexDirection:'column',
                      transition:'box-shadow .15s,transform .15s'}}
              onMouseEnter={e=>{e.currentTarget.style.boxShadow='0 6px 24px rgba(0,51,102,.12)';e.currentTarget.style.transform='translateY(-2px)'}}
              onMouseLeave={e=>{e.currentTarget.style.boxShadow='0 2px 10px rgba(0,0,0,.05)';e.currentTarget.style.transform='translateY(0)'}}>

              {/* Preview image strip */}
              <div style={{display:'grid',
                           gridTemplateColumns: preview.length >= 2 ? '1fr 1fr' : '1fr',
                           height:110,background:'#F4F7FB',
                           borderBottom:'1px solid #E4ECF5',overflow:'hidden'}}>
                {preview.length > 0 ? (
                  preview.slice(0, 4).map((card, i) => (
                    <div key={i}
                      style={{display:'flex',alignItems:'center',justifyContent:'center',
                              padding:10,
                              borderRight: (i % 2 === 0 && preview.length > 1) ? '1px solid #E4ECF5' : 'none',
                              borderBottom: (i < 2 && preview.length > 2) ? '1px solid #E4ECF5' : 'none',
                              background: i % 2 === 0 ? '#F4F7FB' : '#F8FAFC'}}>
                      <img src={card.imageUrl} alt=""
                        style={{maxWidth:'100%',maxHeight:44,objectFit:'contain'}}/>
                    </div>
                  ))
                ) : (
                  <div style={{display:'flex',alignItems:'center',justifyContent:'center',
                               fontSize:36,opacity:.2,gridColumn:'1/-1'}}>🃏</div>
                )}
              </div>

              {/* Info section */}
              <div style={{padding:'14px 16px',flex:1}}>
                <div style={{fontWeight:800,fontSize:15,color:'#1A2E4A',
                             marginBottom:8,lineHeight:1.3}}>
                  {set.title || 'Untitled Set'}
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:5,marginBottom:6}}>
                  <span style={{fontSize:10,fontWeight:700,color:'#1A4A6E',
                                background:'#EBF4FB',padding:'2px 8px',borderRadius:8}}>
                    🃏 {cardCount} card{cardCount !== 1 ? 's' : ''}
                  </span>
                  <span style={{fontSize:10,fontWeight:700,color:'#555',
                                background:'#F4F7FB',padding:'2px 8px',borderRadius:8}}>
                    {CARD_TYPE_LABEL[set.cardType] || set.cardType}
                  </span>
                  <span style={{fontSize:10,fontWeight:700,color:'#555',
                                background:'#F4F7FB',padding:'2px 8px',borderRadius:8}}>
                    {LANG_FLAG[set.language] || ''} {LANG_NAME[set.language] || set.language?.toUpperCase()}
                  </span>
                </div>
                {set.updatedAt && (
                  <div style={{fontSize:10,color:'#B0BECC',marginTop:4}}>
                    Updated {relDate(set.updatedAt)}
                  </div>
                )}
              </div>

              {/* CTA */}
              <div style={{padding:'0 16px 16px'}}>
                <button
                  onClick={() => onNav('learn', { linkedId: set._id, childId: child._id })}
                  style={{width:'100%',padding:'11px',background:'#003366',color:'white',
                          border:'none',borderRadius:10,fontWeight:700,fontSize:13,
                          cursor:'pointer',boxShadow:'0 4px 12px rgba(0,51,102,.25)',
                          display:'flex',alignItems:'center',justifyContent:'center',gap:7,
                          transition:'background .15s'}}
                  onMouseEnter={e=>e.currentTarget.style.background='#1A4A6E'}
                  onMouseLeave={e=>e.currentTarget.style.background='#003366'}>
                  ▶ Start Flip Activity
                </button>
              </div>
            </div>
          )
        })}
      </div>

      {/* Footer note */}
      <div style={{background:'#F8FAFD',borderRadius:10,padding:'12px 16px',
                   border:'1px solid #E4ECF5',display:'flex',alignItems:'center',gap:10}}>
        <span style={{fontSize:16}}>ℹ️</span>
        <div style={{fontSize:11,color:'#7A95B2',lineHeight:1.6}}>
          <strong>Home practice tip:</strong> Use Flip mode to go through the cards with {child.name.split(' ')[0]}.
          Tap each card to reveal the name, then mark whether they got it independently, with a prompt, or needed help.
          Sessions are saved to the progress tracker automatically.
        </div>
      </div>
    </div>
  )
}

// ─── WorkPlanTab ──────────────────────────────────────────────────────────────
function WorkPlanTab({ child, reviewQueue, currentPlan, futurePlans, planLoading, planError, onRefresh }) {
  const [activeDay,setActiveDay] = useState(wpTodayIdx())
  const [selectedFuture,setSelectedFuture] = useState(0)
  useEffect(()=>{ setSelectedFuture(0) }, [child._id])
  const todayIdx   = wpTodayIdx()
  const totalDue   = reviewQueue?.totalDue ?? 0
  const dueGroups  = reviewQueue?.queue ?? []

  if (planLoading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',padding:60,flexDirection:'column',gap:12}}>
      <Spinner size={24}/><div style={{fontSize:13,color:'#9EB0C4'}}>Loading work plan…</div>
    </div>
  )

  const plan = currentPlan

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>

      {/* ── Current week plan ── */}
      {plan ? (
        <div>
          {/* Hero banner */}
          <div style={{background:'linear-gradient(130deg,#0F1A2E 0%,#1D3461 100%)',borderRadius:16,padding:'18px 22px',marginBottom:16,display:'flex',alignItems:'center',gap:18,color:'white'}}>
            <div style={{fontSize:36,flexShrink:0}}>📅</div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:4}}>
                <span style={{width:7,height:7,borderRadius:'50%',background:'#4ADE80',display:'inline-block'}}/>
                <span style={{fontSize:11,fontWeight:800,color:'rgba(255,255,255,.65)',textTransform:'uppercase',letterSpacing:.7}}>Active This Week</span>
              </div>
              <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:18,fontWeight:900,marginBottom:3,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{plan.title}</div>
              <div style={{fontSize:12,color:'rgba(255,255,255,.5)'}}>{wpFormatWeek(plan.weekOf)}</div>
            </div>
            {(()=>{
              const total=plan.days.reduce((s,d)=>s+d.activities.length,0)
              const done=plan.days.reduce((s,d)=>s+d.activities.filter(a=>a.completed).length,0)
              const pct=total>0?Math.round((done/total)*100):0
              return (
                <div style={{textAlign:'center',flexShrink:0,minWidth:80}}>
                  <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:30,fontWeight:900,color:pct===100?'#4ADE80':'#FB923C',lineHeight:1}}>{pct}%</div>
                  <div style={{fontSize:11,color:'rgba(255,255,255,.45)',marginTop:2}}>{done}/{total} done</div>
                  <div style={{marginTop:6,height:5,background:'rgba(255,255,255,.15)',borderRadius:8,overflow:'hidden'}}>
                    <div style={{width:`${pct}%`,height:'100%',background:pct===100?'#4ADE80':'#FB923C',borderRadius:8,transition:'width .5s'}}/>
                  </div>
                </div>
              )
            })()}
            {/* Day dots */}
            <div style={{display:'flex',gap:5,flexShrink:0}}>
              {plan.days.map((d,di)=>{
                const tot=d.activities.length, done=d.activities.filter(a=>a.completed).length
                const isToday=di===todayIdx, allD=tot>0&&done===tot
                return (
                  <div key={di} onClick={()=>setActiveDay(di)} style={{textAlign:'center',cursor:'pointer'}}>
                    <div style={{width:32,height:32,borderRadius:8,background:allD?'rgba(74,222,128,.9)':tot>0?'rgba(255,255,255,.12)':'rgba(255,255,255,.04)',border:isToday?'2.5px solid white':'2px solid transparent',display:'flex',alignItems:'center',justifyContent:'center',fontSize:10,fontWeight:900,color:allD?'#14532D':'white',transition:'all .2s'}}>
                      {allD?'✓':tot===0?'–':`${done}/${tot}`}
                    </div>
                    <div style={{fontSize:8,color:isToday?'white':'rgba(255,255,255,.35)',marginTop:3}}>{WP_SDAYS[di]}</div>
                  </div>
                )
              })}
            </div>
          </div>

          {/* Day tabs */}
          <div style={{display:'flex',gap:6,marginBottom:14,overflowX:'auto',paddingBottom:4}}>
            {plan.days.map((d,di)=>{
              const cnt=d.activities.length, done=d.activities.filter(a=>a.completed).length, isToday=di===todayIdx
              return (
                <button key={di} onClick={()=>setActiveDay(di)} style={{padding:'7px 16px',borderRadius:9,fontWeight:700,fontSize:12,cursor:'pointer',border:activeDay===di?'2px solid #EA580C':'1.5px solid #E4ECF5',background:activeDay===di?'#EA580C':'white',color:activeDay===di?'white':'#3C4F66',position:'relative',whiteSpace:'nowrap',flexShrink:0}}>
                  {isToday&&<span style={{position:'absolute',top:-6,left:'50%',transform:'translateX(-50%)',fontSize:8,background:'#EA580C',color:'#fff',padding:'0 5px',borderRadius:4,fontWeight:700,border:'2px solid white',whiteSpace:'nowrap'}}>TODAY</span>}
                  {WP_SDAYS[di]}
                  {cnt>0&&<span style={{marginLeft:5,fontSize:10,fontWeight:700,color:done===cnt?'#16A34A':activeDay===di?'rgba(255,255,255,.7)':'#8FA5BC'}}>{done}/{cnt}</span>}
                </button>
              )
            })}
          </div>

          {/* Activities */}
          <div style={{display:'flex',flexDirection:'column',gap:10}}>
            {plan.days[activeDay]?.activities.length===0 ? (
              <div style={{background:'white',borderRadius:14,padding:'32px 20px',textAlign:'center',border:'1.5px solid #E4ECF5',color:'#9EB0C4'}}>
                <div style={{fontSize:32,marginBottom:8}}>📅</div>
                <div style={{fontWeight:700,fontSize:13}}>No activities for {WP_DAYS[activeDay]}</div>
              </div>
            ) : plan.days[activeDay].activities.map((act,ai)=>{
              const at=wpGetAType(act.type)
              const isReviewDue=act.type==='flashcard'&&dueGroups.some(g=>{
                const norm=s=>s.toLowerCase().replace(/[^a-z0-9]/g,'')
                return norm(g.setTitle||'').includes(norm(act.title))||norm(act.title).includes(norm(g.setTitle||''))
              })
              return (
                <div key={ai} style={{background:act.completed?'#F0FDF4':'white',borderRadius:13,padding:'14px 16px',border:`1.5px solid ${act.completed?'#86EFAC':'#E4ECF5'}`,borderLeft:`5px solid ${at.color}`}}>
                  <div style={{display:'flex',alignItems:'flex-start',gap:12}}>
                    <span style={{fontSize:22,flexShrink:0}}>{at.icon}</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',alignItems:'center',gap:7,flexWrap:'wrap',marginBottom:5}}>
                        <span style={{fontSize:14,fontWeight:800,color:act.completed?'#166534':'#1A2E4A'}}>{act.title}</span>
                        {act.completed&&<span style={{fontSize:10,fontWeight:700,color:'#166534',background:'#DCFCE7',padding:'1px 8px',borderRadius:8}}>✅ Done</span>}
                        {isReviewDue&&!act.completed&&(
                          <span style={{fontSize:10,fontWeight:700,color:'#E65100',background:'#FFF4EC',border:'1px solid #FFB347',padding:'1px 8px',borderRadius:8}}>⏰ Review due today</span>
                        )}
                        <span style={{marginLeft:'auto',background:'#EBF4FB',color:'#003366',fontSize:11,fontWeight:700,padding:'2px 8px',borderRadius:8,flexShrink:0}}>⏱ {act.duration}</span>
                      </div>
                      {act.notes&&act.notes!==act.title&&<div style={{fontSize:12,color:'#8FA5BC',marginBottom:5}}>📌 {act.notes}</div>}
                      {act.domain&&<span style={{fontSize:10,fontWeight:700,color:'#003366',background:'#EBF4FB',padding:'2px 8px',borderRadius:8}}>{act.domain}</span>}
                      {act._sessionNote&&<div style={{marginTop:8,background:'#EFF6FF',border:'1px solid #BFDBFE',borderRadius:8,padding:'6px 10px',fontSize:11,color:'#1D4ED8'}}>💬 Teacher: {act._sessionNote}</div>}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div style={{background:'white',borderRadius:14,padding:'40px 24px',textAlign:'center',border:'1.5px dashed #E4ECF5',color:'#9EB0C4'}}>
          <div style={{fontSize:40,marginBottom:12}}>📅</div>
          <div style={{fontWeight:800,fontSize:15,marginBottom:6,color:'#1A2E4A'}}>No plan for this week yet</div>
          <div style={{fontSize:12,lineHeight:1.8,maxWidth:320,margin:'0 auto'}}>{planError||`${child.name.split(' ')[0]}'s teacher hasn't set up this week's plan yet. It will appear here once created.`}</div>
        </div>
      )}

      {/* ── SM-2 review suggestions × plan cross-reference ── */}
      {totalDue>0&&(
        <div style={{background:'white',borderRadius:14,padding:'18px 20px',border:'1.5px solid #E4ECF5'}}>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:15,fontWeight:800,color:'#1A2E4A',marginBottom:3}}>
            ⏰ Spaced Repetition — Due Today
          </div>
          <div style={{fontSize:12,color:'#8FA5BC',marginBottom:14}}>
            Cards due for SM-2 review · {totalDue} total · Check if already in this week's plan
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {dueGroups.map((g,i)=>{
              const mp=Math.round((g.masteryAvg||0)*100)
              const inPlan=isInPlan(g.setTitle,plan)
              const mpColor=mp>=70?'#1B6B45':mp>=40?'#D4AC0D':'#C0392B'
              const mpBg=mp>=70?'#F0FFF4':mp>=40?'#FFFDE7':'#FFEBEE'
              return (
                <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 14px',background:'#FAFCFF',borderRadius:10,border:`1.5px solid ${inPlan?'#A8E6C0':'#E8EFF7'}`}}>
                  <span style={{fontSize:22,flexShrink:0}}>🃏</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontWeight:700,fontSize:13,color:'#1A2E4A',marginBottom:5,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g.setTitle||'Untitled Set'}</div>
                    <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
                      <span style={{fontSize:10,fontWeight:700,color:'#E65100',background:'#FFF4EC',padding:'2px 7px',borderRadius:7}}>⏰ {g.dueCount} card{g.dueCount!==1?'s':''} due</span>
                      <span style={{fontSize:10,fontWeight:700,color:mpColor,background:mpBg,padding:'2px 7px',borderRadius:7}}>Mastery {mp}%</span>
                    </div>
                  </div>
                  <div style={{flexShrink:0,textAlign:'right'}}>
                    {inPlan ? (
                      <div>
                        <div style={{fontSize:11,fontWeight:800,color:'#166534',background:'#DCFCE7',border:'1px solid #86EFAC',padding:'4px 11px',borderRadius:8,marginBottom:3}}>✓ In this week's plan</div>
                        <div style={{fontSize:10,color:'#8FA5BC'}}>Teacher has scheduled this</div>
                      </div>
                    ) : (
                      <span style={{fontSize:11,fontWeight:600,color:'#9EB0C4',background:'#F8FAFD',border:'1px solid #E4ECF5',padding:'4px 10px',borderRadius:8}}>Not scheduled yet</span>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
          {dueGroups.length>0&&dueGroups.every(g=>isInPlan(g.setTitle,plan))&&plan&&(
            <div style={{marginTop:12,background:'#F0FFF4',borderRadius:9,padding:'10px 14px',border:'1px solid #86EFAC',display:'flex',alignItems:'center',gap:8}}>
              <span style={{fontSize:16}}>🎉</span>
              <span style={{fontSize:12,fontWeight:700,color:'#166534'}}>All due review activities are already in this week's plan — excellent!</span>
            </div>
          )}
        </div>
      )}

      {/* ── Upcoming week plan ── */}
      {/* ── Future plans (any prepared weeks beyond the current one) ── */}
      <div style={{background:'white',borderRadius:14,padding:'18px 20px',border:'1.5px solid #E4ECF5'}}>
        <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:15,fontWeight:800,color:'#1A2E4A'}}>
            🔮 Future Plans
          </div>
          {onRefresh && (
            <button onClick={onRefresh}
              style={{fontSize:11,fontWeight:700,color:'#3C4F66',background:'#F4F7FB',border:'1px solid #E4ECF5',borderRadius:8,padding:'4px 10px',cursor:'pointer'}}>
              🔄 Refresh
            </button>
          )}
        </div>

        {(!futurePlans || futurePlans.length===0) ? (
          <div style={{background:'#FFFBEB',borderRadius:12,padding:'16px 18px',border:'1.5px solid #FDE68A',display:'flex',alignItems:'center',gap:12}}>
            <span style={{fontSize:28}}>📋</span>
            <div>
              <div style={{fontWeight:700,fontSize:13,color:'#92400E',marginBottom:3}}>No future plans yet</div>
              <div style={{fontSize:12,color:'#B45309',lineHeight:1.7}}>{child.name.split(' ')[0]}'s teacher hasn't created any plans for upcoming weeks yet. They'll appear here once ready.</div>
            </div>
          </div>
        ) : (
          <>
            {/* Week tags — click to view that week's plan */}
            <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:14}}>
              {futurePlans.map((p,i)=>{
                const sel  = selectedFuture===i
                const total= p.days.reduce((s,d)=>s+d.activities.length,0)
                return (
                  <button key={i} onClick={()=>setSelectedFuture(i)}
                    style={{padding:'8px 14px',borderRadius:10,fontWeight:700,fontSize:12,cursor:'pointer',
                            border:`2px solid ${sel?'#1D4ED8':'#BFDBFE'}`,
                            background:sel?'#1D4ED8':'#EFF6FF',
                            color:sel?'white':'#1D4ED8',display:'flex',alignItems:'center',gap:6}}>
                    📅 {wpFormatWeek(p.weekOf)}
                    <span style={{fontSize:10,fontWeight:800,background:sel?'rgba(255,255,255,.2)':'white',padding:'1px 6px',borderRadius:8}}>{total}</span>
                  </button>
                )
              })}
            </div>

            {/* Selected future-week detail */}
            {futurePlans[selectedFuture] && (()=>{
              const fp = futurePlans[selectedFuture]
              return (
                <div style={{background:'#EFF6FF',borderRadius:12,padding:'16px 18px',border:'1.5px solid #BFDBFE'}}>
                  <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:12}}>
                    <span style={{fontSize:11,fontWeight:800,color:'#1D4ED8',background:'white',padding:'2px 10px',borderRadius:8,border:'1px solid #BFDBFE',textTransform:'uppercase',letterSpacing:.6,flexShrink:0}}>
                      Future Plan
                    </span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:800,fontSize:14,color:'#1D4ED8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{fp.title}</div>
                      <div style={{fontSize:12,color:'#3B82F6'}}>{wpFormatWeek(fp.weekOf)}</div>
                    </div>
                  </div>
                  <div style={{display:'flex',flexDirection:'column',gap:8}}>
                    {fp.days.map((d,di)=>(
                      d.activities.length===0 ? null : (
                        <div key={di} style={{background:'white',borderRadius:10,padding:'10px 12px',border:'1px solid #BFDBFE'}}>
                          <div style={{fontSize:11,fontWeight:800,color:'#1D4ED8',marginBottom:6,textTransform:'uppercase',letterSpacing:.6}}>{WP_DAYS[di]}</div>
                          <div style={{display:'flex',flexDirection:'column',gap:6}}>
                            {d.activities.map((act,ai)=>{
                              const at=wpGetAType(act.type)
                              return (
                                <div key={ai} style={{display:'flex',alignItems:'center',gap:8}}>
                                  <span style={{fontSize:16,flexShrink:0}}>{at.icon}</span>
                                  <span style={{flex:1,fontSize:12,fontWeight:700,color:'#1A2E4A',minWidth:0,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{act.title}</span>
                                  <span style={{fontSize:10,fontWeight:700,color:'#003366',background:'#EBF4FB',padding:'2px 8px',borderRadius:8,flexShrink:0}}>⏱ {act.duration}</span>
                                </div>
                              )
                            })}
                          </div>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )
            })()}
          </>
        )}
      </div>
    </div>
  )
}

// ─── Algorithm Panel ──────────────────────────────────────────────────────────
function AlgoPanel({ algData, firstName }) {
  if (!algData) return (
    <div style={{background:'#F8FAFD',border:'1.5px solid #E4ECF5',borderRadius:12,padding:'18px 16px',display:'flex',alignItems:'center',gap:10,color:'#9EB0C4'}}>
      <Spinner size={16} color="#4A90D9"/>
      <span style={{fontSize:12,fontWeight:600}}>Loading algorithm data…</span>
    </div>
  )
  const {queue,sessions,analytics}=algData
  const totalDue=queue?.totalDue??0, groups=queue?.queue??[], lastSess=sessions?.[0], lastAcc=sessAcc(lastSess), caught=totalDue===0
  const bktSets=analytics?.masteryBySet||[]
  return (
    <div style={{background:'#F8FAFD',border:'1.5px solid #E4ECF5',borderRadius:12,overflow:'hidden'}}>
      <div style={{padding:'10px 16px',borderBottom:'1px solid #E8EFF7',display:'flex',alignItems:'center',gap:8}}>
        <span style={{fontSize:14}}>🧠</span>
        <span style={{fontSize:10,fontWeight:800,color:'#7A95B2',textTransform:'uppercase',letterSpacing:.8}}>Algorithm Data · SM-2 · BKT · Elo</span>
      </div>
      <div style={{padding:'14px 16px',display:'flex',flexDirection:'column',gap:14}}>
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
          <div style={{borderRadius:10,padding:'10px 12px',textAlign:'center',background:caught?'#F0FFF4':'#FFF4EC',border:`1.5px solid ${caught?'#A8E6C0':'#FFB347'}`}}>
            <div style={{fontSize:22,fontWeight:900,color:caught?'#1B6B45':'#E65100',lineHeight:1,marginBottom:3}}>{caught?'✓':totalDue}</div>
            <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:.6,color:caught?'#1B6B45':'#E65100'}}>{caught?'Caught up':'Cards Due'}</div>
          </div>
          <div style={{borderRadius:10,padding:'10px 12px',textAlign:'center',background:'#EBF4FB',border:'1.5px solid #BDD7EE'}}>
            <div style={{fontSize:22,fontWeight:900,color:'#003366',lineHeight:1,marginBottom:3}}>{sessions?.length??0}</div>
            <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:.6,color:'#1A4A6E'}}>Sessions</div>
          </div>
          <div style={{borderRadius:10,padding:'10px 12px',textAlign:'center',background:accBg(lastAcc),border:`1.5px solid ${accColor(lastAcc)}40`}}>
            <div style={{fontSize:22,fontWeight:900,color:accColor(lastAcc),lineHeight:1,marginBottom:3}}>{lastAcc!=null?`${lastAcc}%`:'—'}</div>
            <div style={{fontSize:9,fontWeight:800,textTransform:'uppercase',letterSpacing:.6,color:accColor(lastAcc)}}>Last Acc.</div>
          </div>
        </div>
        {groups.length>0&&(
          <div>
            <div style={{fontSize:10,fontWeight:800,color:'#7A95B2',textTransform:'uppercase',letterSpacing:.7,marginBottom:8}}>Spaced Repetition — Due by Set</div>
            {groups.map((g,i)=>{const mp=Math.round((g.masteryAvg||0)*100);return(
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'8px 10px',marginBottom:5,background:'white',borderRadius:8,border:'1px solid #E8EFF7'}}>
                <span style={{fontSize:14,flexShrink:0}}>🃏</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#1A2E4A',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{g.setTitle||'Untitled Set'}</div>
                  <Bar pct={mp} color={accColor(mp)} h={3}/>
                </div>
                <div style={{display:'flex',flexDirection:'column',alignItems:'flex-end',gap:2,flexShrink:0}}>
                  <Pill label={`⏰ ${g.dueCount}`} color="#E65100" bg="#FFF4EC" size={9}/>
                  <Pill label={`BKT ${mp}%`} color={accColor(mp)} size={9}/>
                </div>
              </div>
            )})}
          </div>
        )}
        {bktSets.length>0&&(
          <div>
            <div style={{fontSize:10,fontWeight:800,color:'#7A95B2',textTransform:'uppercase',letterSpacing:.7,marginBottom:8}}>BKT Mastery — Card Level</div>
            {bktSets.slice(0,2).map(set=>{const am=Math.round((set.avgMastery||0)*100);return(
              <div key={String(set.setId)} style={{background:'white',borderRadius:9,padding:'10px 12px',marginBottom:6,border:'1px solid #E8EFF7'}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:8}}>
                  <span style={{fontSize:11,fontWeight:700,color:'#1A2E4A'}}>🃏 {set.setTitle}</span>
                  <Pill label={`Avg ${am}%`} color={accColor(am)} size={9}/>
                </div>
                <div style={{display:'flex',flexWrap:'wrap',gap:4}}>
                  {(set.cards||[]).slice(0,8).map(c=>{const mp=Math.round((c.masteryProb||0)*100);return(
                    <div key={c.cardId} style={{background:accBg(mp),border:`1px solid ${accColor(mp)}40`,borderRadius:6,padding:'3px 7px',fontSize:9,fontWeight:700,color:accColor(mp)}}>{c.cardLabel||c.cardId}: {mp}%</div>
                  )})}
                  {(set.cards||[]).length>8&&<span style={{fontSize:9,color:'#9EB0C4'}}>+{set.cards.length-8} more</span>}
                </div>
              </div>
            )})}
          </div>
        )}
        {sessions?.length>0&&(
          <div>
            <div style={{fontSize:10,fontWeight:800,color:'#7A95B2',textTransform:'uppercase',letterSpacing:.7,marginBottom:8}}>Recent Sessions</div>
            {sessions.slice(0,4).map((s,i)=>{const acc=sessAcc(s),mIcon=MODE_ICON[s.moduleType]||'📌';return(
              <div key={i} style={{display:'flex',alignItems:'center',gap:9,padding:'7px 10px',marginBottom:4,background:'white',borderRadius:8,border:'1px solid #E8EFF7'}}>
                <span style={{fontSize:15,width:22,textAlign:'center',flexShrink:0}}>{mIcon}</span>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{fontSize:11,fontWeight:700,color:'#1A2E4A',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.setTitle||s.moduleType||'Session'}</div>
                  <div style={{fontSize:9,color:'#8FA5BC',marginTop:1}}>{relDate(s.createdAt)} · {s.ratedCount??s.cardCount??0} cards</div>
                </div>
                {acc!=null&&<Pill label={`${acc}%`} color={accColor(acc)} bg={accBg(acc)} size={10}/>}
              </div>
            )})}
          </div>
        )}
        {!groups.length&&!sessions?.length&&<div style={{textAlign:'center',padding:'8px 0 4px',fontSize:11,color:'#9EB0C4'}}>No flashcard sessions yet for {firstName}.</div>}
      </div>
    </div>
  )
}

// ─── SM-2 Schedule Tab ────────────────────────────────────────────────────────
function ScheduleTab({ child, reviewQueue, sessions, onNav, algoResults }) {
  const totalDue=reviewQueue?.totalDue??0, groups=reviewQueue?.queue??[], caught=totalDue===0
  const today=new Date(), weekDays=Array.from({length:7},(_,i)=>{const d=new Date(today);d.setDate(today.getDate()+i);return d})
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:20,alignItems:'start'}}>
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div style={{background:caught?'linear-gradient(130deg,#1B6B45 0%,#27AE60 100%)':'linear-gradient(130deg,#C0392B 0%,#E65100 100%)',borderRadius:16,padding:'22px 26px',color:'white',display:'flex',alignItems:'center',gap:18}}>
          <div style={{fontSize:44,lineHeight:1,flexShrink:0}}>{caught?'✅':'⏰'}</div>
          <div style={{flex:1}}>
            <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:22,fontWeight:900,marginBottom:5,lineHeight:1.2}}>
              {caught?`${child.name.split(' ')[0]} is all caught up!`:`${totalDue} flashcards due today`}
            </div>
            <div style={{fontSize:12,opacity:.85,lineHeight:1.5}}>
              {caught?'No spaced repetition cards are due right now.':`Remind ${child.name.split(' ')[0]}'s teacher to schedule a review session today.`}
            </div>
          </div>
        </div>
        <div style={{background:'white',borderRadius:14,padding:'18px 20px',border:'1.5px solid #E4ECF5'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
            <div><div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:15,fontWeight:800,color:'#1A2E4A'}}>Due Today by Set</div><div style={{fontSize:11,color:'#8FA5BC',marginTop:2}}>{groups.length} flashcard set{groups.length!==1?'s':''}</div></div>
            {totalDue>0&&<Pill label={`${totalDue} total`} color="#E65100" bg="#FFF4EC"/>}
          </div>
          {groups.length===0?<EmptyState icon="✅" text="No cards due — all reviews are on track!"/>:(
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {groups.map((g,i)=>{const mp=Math.round((g.masteryAvg||0)*100);return(
                <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:'11px 14px',background:'#FAFCFF',borderRadius:10,border:'1px solid #E8EFF7'}}>
                  <span style={{fontSize:20,flexShrink:0}}>🃏</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:800,color:'#1A2E4A'}}>{g.setTitle||'Untitled Set'}</div>
                    <div style={{marginTop:4}}><Bar pct={mp} color={accColor(mp)} h={4}/></div>
                  </div>
                  <div style={{display:'flex',gap:5,flexShrink:0}}>
                    <Pill label={`⏰ ${g.dueCount} cards`} color="#E65100" bg="#FFF4EC" size={10}/>
                    <Pill label={`BKT ${mp}%`} color={accColor(mp)} size={10}/>
                  </div>
                </div>
              )})}
            </div>
          )}
        </div>
        <div style={{background:'white',borderRadius:14,padding:'18px 20px',border:'1.5px solid #E4ECF5'}}>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:15,fontWeight:800,color:'#1A2E4A',marginBottom:4}}>7-Day Review Calendar</div>
          <div style={{fontSize:11,color:'#8FA5BC',marginBottom:14}}>Spaced repetition schedule this week</div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:6}}>
            {weekDays.map((d,i)=>{const isToday=i===0,dayDue=i===0?totalDue:0;return(
              <div key={i} style={{textAlign:'center',background:isToday?(caught?'#F0FFF4':'#FFF4EC'):'#F8FAFD',borderRadius:9,padding:'10px 6px',border:`1.5px solid ${isToday?(caught?'#A8E6C0':'#FFB347'):'#E4ECF5'}`}}>
                <div style={{fontSize:9,fontWeight:700,color:isToday?'#003366':'#8FA5BC',textTransform:'uppercase',marginBottom:4}}>{d.toLocaleDateString('en-IN',{weekday:'short'})}</div>
                <div style={{fontSize:13,fontWeight:900,color:isToday?(caught?'#1B6B45':'#E65100'):'#B0BECC',lineHeight:1}}>{isToday?(caught?'✓':dayDue):'–'}</div>
                <div style={{fontSize:8,color:'#9EB0C4',marginTop:2}}>{d.getDate()}</div>
              </div>
            )})}
          </div>
        </div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        {algoResults&&algoResults.length>0&&(
          <div style={{background:'white',borderRadius:14,padding:'16px 18px',border:'1.5px solid #E4ECF5'}}>
            <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,color:'#1A2E4A',marginBottom:4}}>🎯 Personalised Suggestions</div>
            <div style={{fontSize:11,color:'#8FA5BC',marginBottom:12}}>Based on BKT mastery · SM-2 schedule · Elo performance</div>
            <div style={{display:'flex',flexDirection:'column',gap:7}}>
              {algoResults.slice(0,6).map((r,i)=>{
                const mp=Math.round((r.avgMasteryProb||0)*100)
                const PSTYLE={review:{color:'#E65100',bg:'#FFF4EC',icon:'⏰'},practice:{color:'#7B2D8B',bg:'#F5F0FF',icon:'🎯'},reinforce:{color:'#1D4ED8',bg:'#EFF6FF',icon:'💪'},maintain:{color:'#16A34A',bg:'#F0FFF4',icon:'✅'}}
                const ps=PSTYLE[r.priority]||PSTYLE.maintain
                return(
                  <div key={i} style={{display:'flex',alignItems:'center',gap:8,padding:'9px 11px',background:'#FAFCFF',borderRadius:9,border:`1.5px solid ${ps.bg}`}}>
                    <span style={{fontSize:18,flexShrink:0}}>🃏</span>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:11,fontWeight:700,color:'#1A2E4A',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{r.setTitle||'Untitled Set'}</div>
                      <div style={{fontSize:10,color:'#8FA5BC',marginTop:1}}>{r.suggestionNote||''}</div>
                    </div>
                    <div style={{flexShrink:0,textAlign:'right'}}>
                      <div style={{fontSize:10,fontWeight:700,color:ps.color,background:ps.bg,padding:'2px 7px',borderRadius:6}}>{ps.icon} {mp}%</div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}
        <div style={{background:'linear-gradient(135deg,#1A2E4A 0%,#1A4A6E 100%)',borderRadius:14,padding:18,color:'white'}}>
          <div style={{fontSize:24,marginBottom:10}}>🔬</div>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,marginBottom:8}}>About SM-2 Algorithm</div>
          <div style={{fontSize:11,lineHeight:1.7,opacity:.85}}>The SM-2 spaced repetition algorithm schedules each flashcard for review at the <strong>optimal moment</strong> — just before the child is likely to forget it.</div>
          <div style={{marginTop:12,display:'flex',flexDirection:'column',gap:6}}>
            {[{icon:'🧠',label:'BKT tracks mastery probability per card'},{icon:'📈',label:'Elo rating adapts difficulty dynamically'},{icon:'⏰',label:'Reviews are spaced to maximise retention'}].map((item,i)=>(
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,background:'rgba(255,255,255,.08)',borderRadius:7,padding:'6px 10px'}}>
                <span style={{fontSize:12}}>{item.icon}</span><span style={{fontSize:10,fontWeight:600}}>{item.label}</span>
              </div>
            ))}
          </div>
        </div>
        <div style={{background:'white',borderRadius:14,padding:'16px 18px',border:'1.5px solid #E4ECF5'}}>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,color:'#1A2E4A',marginBottom:12}}>📊 Recent Accuracy Trend</div>
          {sessions.length<2?<EmptyState icon="📈" text="Need 2+ sessions to show trend"/>:(
            <div style={{display:'flex',alignItems:'flex-end',gap:4,height:60}}>
              {sessions.slice(0,10).reverse().map((s,i)=>{const acc=sessAcc(s)??0;return(
                <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:2}}>
                  <div style={{width:'100%',background:acc>=70?'#E8F5E9':acc>=40?'#FFF9C4':'#FFEBEE',borderTop:`3px solid ${accColor(acc)}`,height:`${Math.max(4,acc*.55)}px`,borderRadius:'2px 2px 0 0'}} title={`${acc}%`}/>
                  <div style={{fontSize:7,color:'#bbb'}}>{new Date(s.createdAt).getDate()}</div>
                </div>
              )})}
            </div>
          )}
        </div>
        <div style={{background:'white',borderRadius:14,padding:'16px 18px',border:'1.5px solid #E4ECF5'}}>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,color:'#1A2E4A',marginBottom:12}}>🎮 Learning Modules</div>
          {[{icon:'🃏',label:'Flashcard Activity',id:'learn'},{icon:'📝',label:'Worksheet Activity',id:'worksheet-learn'},{icon:'🖼️',label:'Activity Cards',id:'activity-learn'},{icon:'📖',label:'Story Book',id:'storybook-learn'}].map((m,i)=>(
            <button key={i} onClick={()=>onNav(m.id)} style={{width:'100%',display:'flex',alignItems:'center',gap:9,padding:'8px 11px',marginBottom:5,background:'#F8FAFD',border:'none',borderRadius:8,cursor:'pointer'}}
              onMouseEnter={e=>e.currentTarget.style.background='#EEF3FA'} onMouseLeave={e=>e.currentTarget.style.background='#F8FAFD'}>
              <span style={{fontSize:16}}>{m.icon}</span><span style={{fontSize:12,fontWeight:600,color:'#3C4F66',flex:1,textAlign:'left'}}>{m.label}</span><span style={{fontSize:11,color:'#9EB0C4'}}>→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Progress Tab ─────────────────────────────────────────────────────────────
function ProgressTab({ record, child }) {
  const [expandedDomain,setExpandedDomain]=useState(null)
  const domainScores=DOMAINS.map(d=>({...d,pct:domainPct(record,d.id)}))
  const weakInds=ALL_INDICATORS.filter(ind=>(LEVELS[record?.[`${ind.domainId}::${ind.id}`]?.level]?.score??0)<=1)
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 280px',gap:20,alignItems:'start'}}>
      <div>
        {domainScores.map(domain=>{
          const expanded=expandedDomain===domain.id
          const inds=ALL_INDICATORS.filter(i=>i.domainId===domain.id)
          return (
            <div key={domain.id} style={{background:'white',borderRadius:12,marginBottom:10,border:`1px solid ${domain.border}30`,overflow:'hidden'}}>
              <button onClick={()=>setExpandedDomain(expanded?null:domain.id)} style={{display:'flex',alignItems:'center',gap:12,width:'100%',padding:'14px 16px',background:domain.bg,border:'none',cursor:'pointer',textAlign:'left'}}>
                <span style={{fontSize:20}}>{domain.icon}</span>
                <div style={{flex:1}}><div style={{fontWeight:800,fontSize:13,color:domain.color}}>{domain.label}</div><Bar pct={domain.pct} color={domain.color} h={4}/></div>
                <div style={{fontWeight:900,fontSize:20,color:domain.color,minWidth:48,textAlign:'right'}}>{domain.pct}%</div>
                <span style={{color:domain.color,fontSize:12}}>{expanded?'▲':'▼'}</span>
              </button>
              {expanded&&(
                <div style={{padding:'12px 16px'}}>
                  {inds.map(ind=>{
                    const key=`${domain.id}::${ind.id}`,lv=record?.[key]?.level||'not-observed',lvl=LEVELS[lv]
                    return (
                      <div key={ind.id} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 0',borderBottom:'1px solid #F0F2F8'}}>
                        <div style={{flex:1,fontSize:12,color:'#3C4F66',lineHeight:1.4}}>{ind.label}</div>
                        <div style={{display:'flex',gap:3,flexShrink:0}}>
                          {Object.entries(LEVELS).map(([lId,l])=>(
                            <div key={lId} style={{width:28,height:28,borderRadius:7,background:lv===lId?l.bg:'#F5F7FA',border:`1.5px solid ${lv===lId?l.color:'#E0E7F0'}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:800,color:lv===lId?l.color:'#C0CDD8'}}>{l.short}</div>
                          ))}
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })}
        {Object.keys(record).length===0&&<div style={{textAlign:'center',padding:32,background:'white',borderRadius:12,border:'1.5px dashed #E4EBF2',color:'#9E9E9E',fontSize:12}}><div style={{fontSize:32,marginBottom:8}}>📋</div>Progress data hasn't been recorded yet.</div>}
        <div style={{display:'flex',gap:10,flexWrap:'wrap',marginTop:10,padding:'10px 0'}}>
          {Object.entries(LEVELS).map(([,l])=>(
            <div key={l.short} style={{display:'flex',alignItems:'center',gap:5}}>
              <div style={{width:22,height:22,borderRadius:5,background:l.bg,border:`1.5px solid ${l.color}`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:9,fontWeight:800,color:l.color}}>{l.short}</div>
              <span style={{fontSize:10,color:'#888'}}>{l.label}</span>
            </div>
          ))}
        </div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div style={{background:'white',borderRadius:13,padding:'16px 18px',border:'1.5px solid #E4ECF5'}}>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,color:'#1A2E4A',marginBottom:12}}>🎯 Focus Areas</div>
          {weakInds.length===0?<div style={{fontSize:12,color:'#1B6B45',fontWeight:700}}>✅ All indicators at Developing or Achieved</div>:(
            <div style={{display:'flex',flexDirection:'column',gap:5}}>
              {weakInds.slice(0,8).map(ind=>{
                const lv=record?.[`${ind.domainId}::${ind.id}`]?.level||'not-observed',lvl=LEVELS[lv],dom=DOMAINS.find(d=>d.id===ind.domainId)
                return (
                  <div key={`${ind.domainId}::${ind.id}`} style={{display:'flex',alignItems:'center',gap:8,background:'#F8FAFD',borderRadius:8,padding:'7px 10px',border:'1px solid #E8EFF7'}}>
                    <span style={{fontSize:12}}>{dom?.icon}</span>
                    <span style={{flex:1,fontSize:11,color:'#3C4F66',lineHeight:1.4}}>{ind.label}</span>
                    <span style={{fontSize:9,fontWeight:800,color:lvl.color,background:lvl.bg,padding:'2px 6px',borderRadius:5}}>{lvl.short}</span>
                  </div>
                )
              })}
              {weakInds.length>8&&<div style={{fontSize:11,color:'#9EB0C4',textAlign:'center'}}>+{weakInds.length-8} more</div>}
            </div>
          )}
        </div>
        {child.iepGoals&&<div style={{background:'#EBF4FB',borderRadius:13,padding:'16px 18px',borderLeft:'3px solid #4A90D9'}}><div style={{fontSize:9,fontWeight:800,color:'#003366',textTransform:'uppercase',letterSpacing:.8,marginBottom:8}}>🎯 IEP Goals</div><div style={{fontSize:12,color:'#3C4F66',lineHeight:1.7}}>{child.iepGoals}</div></div>}
        <div style={{background:'#F8FAFD',borderRadius:10,padding:'11px 14px',border:'1px solid #E4ECF5'}}><div style={{fontSize:10,color:'#9EB0C4',lineHeight:1.6}}><strong style={{color:'#7A95B2'}}>ℹ️ Parent View</strong>{' '}This is a read-only view.</div></div>
      </div>
    </div>
  )
}

// ─── Sessions Tab ─────────────────────────────────────────────────────────────
function SessionsTab({ sessions, child }) {
  const [filter, setFilter] = useState('all')

  const filtered = filter==='all' ? sessions
    : sessions.filter(s => filter==='parent'
        ? s.conductorRole==='parent'
        : s.conductorRole!=='parent')

  const teacherSess = sessions.filter(s=>s.conductorRole!=='parent')
  const parentSess  = sessions.filter(s=>s.conductorRole==='parent')

  const weekStart = new Date(); weekStart.setDate(weekStart.getDate()-weekStart.getDay()+(weekStart.getDay()===0?-6:1)); weekStart.setHours(0,0,0,0)
  const thisWeekParent  = parentSess.filter(s=>new Date(s.createdAt)>=weekStart).length
  const thisWeekTeacher = teacherSess.filter(s=>new Date(s.createdAt)>=weekStart).length

  const byModule=filtered.reduce((acc,s)=>{const t=s.moduleType||'other';acc[t]=(acc[t]||0)+1;return acc},{})
  const avgAcc=(()=>{const rated=filtered.filter(s=>(s.ratedCount||s.correctCount)>0);if(!rated.length)return null;return Math.round(rated.reduce((a,s)=>a+(s.correctCount||0)/Math.max(s.ratedCount||1,1),0)/rated.length*100)})()

  const ROLE_COLOR = { parent:'#7B2D8B', teacher:'#1A4A6E', independent:'#059669' }
  const ROLE_LABEL = { parent:'🏠 Home', teacher:'🏫 School', independent:'Self' }

  return (
    <div style={{display:'flex',flexDirection:'column',gap:20}}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
        <div style={{background:'linear-gradient(135deg,#7B2D8B,#9333EA)',borderRadius:14,padding:'18px 20px',color:'white',cursor:'pointer',border:filter==='parent'?'3px solid white':'3px solid transparent'}} onClick={()=>setFilter(f=>f==='parent'?'all':'parent')}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
            <span style={{fontSize:24}}>🏠</span>
            <div>
              <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:22,fontWeight:900,lineHeight:1}}>{parentSess.length}</div>
              <div style={{fontSize:11,opacity:.8}}>Home Practice Sessions</div>
            </div>
          </div>
          <div style={{fontSize:12,opacity:.75}}>This week: <strong>{thisWeekParent}</strong> session{thisWeekParent!==1?'s':''}</div>
          <div style={{marginTop:8,height:4,background:'rgba(255,255,255,.2)',borderRadius:4,overflow:'hidden'}}>
            <div style={{width:sessions.length>0?`${Math.round(parentSess.length/sessions.length*100)}%`:'0%',height:'100%',background:'white',borderRadius:4}}/>
          </div>
        </div>
        <div style={{background:'linear-gradient(135deg,#1A4A6E,#2563EB)',borderRadius:14,padding:'18px 20px',color:'white',cursor:'pointer',border:filter==='teacher'?'3px solid white':'3px solid transparent'}} onClick={()=>setFilter(f=>f==='teacher'?'all':'teacher')}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
            <span style={{fontSize:24}}>🏫</span>
            <div>
              <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:22,fontWeight:900,lineHeight:1}}>{teacherSess.length}</div>
              <div style={{fontSize:11,opacity:.8}}>School Sessions</div>
            </div>
          </div>
          <div style={{fontSize:12,opacity:.75}}>This week: <strong>{thisWeekTeacher}</strong> session{thisWeekTeacher!==1?'s':''}</div>
          <div style={{marginTop:8,height:4,background:'rgba(255,255,255,.2)',borderRadius:4,overflow:'hidden'}}>
            <div style={{width:sessions.length>0?`${Math.round(teacherSess.length/sessions.length*100)}%`:'0%',height:'100%',background:'white',borderRadius:4}}/>
          </div>
        </div>
      </div>

      <div style={{display:'flex',gap:6}}>
        {[['all','All Sessions'],['teacher','🏫 School Only'],['parent','🏠 Home Only']].map(([v,l])=>(
          <button key={v} onClick={()=>setFilter(v)} style={{padding:'6px 14px',borderRadius:8,fontWeight:700,fontSize:12,cursor:'pointer',border:`1.5px solid ${filter===v?'#1A2E4A':'#D9E4F0'}`,background:filter===v?'#1A2E4A':'white',color:filter===v?'white':'#3C4F66'}}>
            {l}
          </button>
        ))}
        <div style={{marginLeft:'auto',fontSize:12,color:'#8FA5BC',alignSelf:'center'}}>{filtered.length} sessions shown</div>
      </div>

      <div style={{display:'grid',gridTemplateColumns:'1fr 260px',gap:20,alignItems:'start'}}>
        <div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:12,marginBottom:18}}>
            {[
              {icon:'📅',val:filtered.length,label:'Sessions',color:'#1A4A6E'},
              {icon:'🎯',val:avgAcc!=null?`${avgAcc}%`:'—',label:'Avg Accuracy',color:accColor(avgAcc)},
              {icon:'📆',val:filtered[0]?.createdAt?relDate(filtered[0].createdAt):'—',label:'Last Session',color:'#7B2D8B'},
            ].map((s,i)=>(
              <div key={i} style={{background:'white',borderRadius:12,padding:'14px 16px',border:'1.5px solid #E4ECF5',textAlign:'center'}}>
                <div style={{fontSize:22,marginBottom:6}}>{s.icon}</div>
                <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:900,color:s.color,lineHeight:1}}>{s.val}</div>
                <div style={{fontSize:10,fontWeight:700,color:'#9E9E9E',marginTop:3}}>{s.label}</div>
              </div>
            ))}
          </div>

          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:15,fontWeight:800,color:'#1A2E4A',marginBottom:12}}>Session History</div>
          {filtered.length===0?<EmptyState icon="📋" text="No sessions for this filter"/>:(
            <div style={{display:'flex',flexDirection:'column',gap:7}}>
              {filtered.map(s=>{
                const acc=sessAcc(s),mIcon=MODE_ICON[s.moduleType]||'📌'
                const role=s.conductorRole||'teacher'
                const roleLbl=ROLE_LABEL[role]||'School'
                const roleCol=ROLE_COLOR[role]||'#1A4A6E'
                return(
                  <div key={s._id} style={{display:'flex',alignItems:'center',gap:12,background:'white',borderRadius:10,padding:'11px 14px',border:'1.5px solid #E4EBF2'}}>
                    <div style={{width:36,height:36,borderRadius:9,background:'#F4F6FB',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>{mIcon}</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:700,fontSize:12,color:'#1A1A2E',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.setTitle||s.moduleType||'Session'}</div>
                      <div style={{fontSize:10,color:'#9E9E9E',marginTop:1,display:'flex',gap:8,alignItems:'center'}}>
                        {s.durationMin?<span>{s.durationMin} min</span>:null}
                        <span>{relDate(s.createdAt)}</span>
                        <span style={{fontSize:9,fontWeight:700,color:roleCol,background:`${roleCol}15`,padding:'1px 6px',borderRadius:5}}>{roleLbl}</span>
                      </div>
                    </div>
                    {acc!=null&&<div style={{fontFamily:"'Playfair Display',serif",fontSize:16,fontWeight:900,color:accColor(acc),flexShrink:0}}>{acc}%</div>}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:12}}>
          <div style={{background:'white',borderRadius:13,padding:'16px 18px',border:'1.5px solid #E4ECF5'}}>
            <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,color:'#1A2E4A',marginBottom:12}}>Module Breakdown</div>
            {Object.keys(byModule).length===0?<EmptyState icon="📊" text="No data yet"/>:(
              Object.entries(byModule).map(([mod,count])=>{
                const mIcon=MODE_ICON[mod]||'📌',mLabel={flashcard:'Flashcards',worksheet:'Worksheets',activitycard:'Activity Cards',storybook:'Story Books'}[mod]||mod,pct=Math.round(count/filtered.length*100)
                return (
                  <div key={mod} style={{marginBottom:12}}>
                    <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:5}}>
                      <span style={{fontSize:14}}>{mIcon}</span>
                      <span style={{flex:1,fontSize:11.5,fontWeight:600,color:'#3C4F66'}}>{mLabel}</span>
                      <span style={{fontSize:12,fontWeight:900,color:'#1A4A6E'}}>{count}</span>
                    </div>
                    <Bar pct={pct} color="#1A4A6E" h={5}/>
                  </div>
                )
              })
            )}
          </div>
          <div style={{background:'white',borderRadius:13,padding:'16px 18px',border:'1.5px solid #E4ECF5'}}>
            <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,color:'#1A2E4A',marginBottom:12}}>This Week</div>
            {[['🏠 Home Practice',thisWeekParent,'#7B2D8B'],['🏫 School Sessions',thisWeekTeacher,'#1A4A6E']].map(([l,v,c])=>(
              <div key={l} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'8px 0',borderBottom:'1px solid #F0F5FC',fontSize:13}}>
                <span style={{color:'#3C4F66'}}>{l}</span>
                <span style={{fontWeight:900,color:c,fontSize:16}}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

// ─── Home Tips Tab ────────────────────────────────────────────────────────────
function TipsTab({ record }) {
  const domainScores=DOMAINS.map(d=>({...d,pct:domainPct(record,d.id)})).sort((a,b)=>a.pct-b.pct)
  return (
    <div style={{display:'flex',flexDirection:'column',gap:14}}>
      {domainScores.map(d=>(
        <div key={d.id} style={{background:d.bg,borderRadius:14,border:`1.5px solid ${d.border}`,padding:'16px 18px'}}>
          <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:12}}>
            <span style={{fontSize:22}}>{d.icon}</span>
            <div><div style={{fontWeight:800,fontSize:13,color:d.color}}>{d.label}</div><div style={{fontSize:10,color:d.color,opacity:.7}}>{d.pct}% progress</div></div>
            {d.pct<40&&<span style={{marginLeft:'auto',fontSize:9,fontWeight:800,color:'#C62828',background:'#FFEBEE',padding:'2px 8px',borderRadius:5}}>PRIORITY</span>}
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:7}}>
            {(HOME_TIPS[d.id]||[]).map((tip,i)=>(
              <div key={i} style={{background:'rgba(255,255,255,.7)',borderRadius:8,padding:'8px 12px',fontSize:12,color:'#333',fontWeight:500,lineHeight:1.5}}>{tip}</div>
            ))}
          </div>
        </div>
      ))}
      <div style={{background:'white',borderRadius:12,padding:'16px 18px',border:'1.5px solid #E4EBF2',textAlign:'center'}}>
        <div style={{fontSize:28,marginBottom:8}}>❤️</div>
        <div style={{fontSize:13,fontWeight:700,color:'#1A4A6E',marginBottom:4}}>Every small step counts!</div>
        <div style={{fontSize:11,color:'#9E9E9E',lineHeight:1.7}}>Consistent daily practice at home, even for 10–15 minutes, makes a significant difference in your child's development.</div>
      </div>
    </div>
  )
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────
function OverviewTab({ child, record, sessions, algData, reviewQueue, onNav }) {
  const domainScores=DOMAINS.map(d=>({...d,pct:domainPct(record,d.id)}))
  const sorted=[...domainScores].sort((a,b)=>b.pct-a.pct)
  const strongest=sorted[0], weakest=sorted[sorted.length-1]
  const overall=overallPct(record), totalDue=reviewQueue?.totalDue??0
  const avgAcc=(()=>{const rated=sessions.filter(s=>s.ratedCount>0);if(!rated.length)return null;return Math.round(rated.reduce((a,s)=>a+(s.correctCount||0)/s.ratedCount,0)/rated.length*100)})()
  return (
    <div style={{display:'grid',gridTemplateColumns:'1fr 300px',gap:20,alignItems:'start'}}>
      <div style={{display:'flex',flexDirection:'column',gap:16}}>
        <div style={{background:'linear-gradient(135deg,#0D2240 0%,#1A4A6E 100%)',borderRadius:18,padding:'22px 26px',color:'white'}}>
          <div style={{display:'flex',alignItems:'center',gap:18,marginBottom:18}}>
            <Avatar name={child.name} color={child.color||'#4A90D9'} size={60} ring={overall}/>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:24,fontWeight:900,color:'white',marginBottom:6,lineHeight:1.1}}>{child.name}</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:5}}>
                {calcAge(child.dob)!=null&&<span style={{fontSize:10,fontWeight:700,color:'rgba(255,255,255,.75)',background:'rgba(255,255,255,.12)',padding:'2px 9px',borderRadius:20}}>Age {calcAge(child.dob)}</span>}
                {(child.disabilities||[]).map(d=><span key={d} style={{fontSize:10,fontWeight:700,color:'rgba(255,255,255,.75)',background:'rgba(255,255,255,.12)',padding:'2px 9px',borderRadius:20}}>{d}</span>)}
              </div>
            </div>
            <div style={{textAlign:'center',flexShrink:0}}>
              <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:38,fontWeight:900,color:'white',lineHeight:1}}>{overall}%</div>
              <div style={{fontSize:9,color:'rgba(255,255,255,.5)',fontWeight:600}}>OVERALL</div>
            </div>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:8,marginBottom:16}}>
            {domainScores.map(d=>(
              <div key={d.id} style={{background:'rgba(255,255,255,.09)',borderRadius:9,padding:'8px 5px',textAlign:'center'}}>
                <div style={{fontSize:16,marginBottom:3}}>{d.icon}</div>
                <div style={{fontSize:14,fontWeight:900,color:'white',lineHeight:1}}>{d.pct}%</div>
                <div style={{fontSize:8,color:'rgba(255,255,255,.45)',marginTop:2}}>{d.short}</div>
              </div>
            ))}
          </div>
          <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
            {strongest&&<div style={{background:'rgba(255,255,255,.12)',borderRadius:9,padding:'8px 12px',flex:1,minWidth:100}}><div style={{fontSize:8,color:'rgba(255,255,255,.5)',fontWeight:700,marginBottom:3}}>STRONGEST</div><div style={{display:'flex',alignItems:'center',gap:5}}><span style={{fontSize:13}}>{strongest.icon}</span><span style={{fontSize:11,fontWeight:800,color:'white'}}>{strongest.label.split(' ')[0]}</span><span style={{fontSize:13,fontWeight:900,color:'#A8E6CF',marginLeft:4}}>{strongest.pct}%</span></div></div>}
            {weakest&&weakest.id!==strongest?.id&&<div style={{background:'rgba(255,255,255,.12)',borderRadius:9,padding:'8px 12px',flex:1,minWidth:100}}><div style={{fontSize:8,color:'rgba(255,255,255,.5)',fontWeight:700,marginBottom:3}}>FOCUS AREA</div><div style={{display:'flex',alignItems:'center',gap:5}}><span style={{fontSize:13}}>{weakest.icon}</span><span style={{fontSize:11,fontWeight:800,color:'white'}}>{weakest.label.split(' ')[0]}</span><span style={{fontSize:13,fontWeight:900,color:'#FFCDD2',marginLeft:4}}>{weakest.pct}%</span></div></div>}
            {totalDue>0&&<div style={{background:'rgba(230,81,0,.3)',borderRadius:9,padding:'8px 12px',border:'1px solid rgba(255,179,71,.4)'}}><div style={{fontSize:8,color:'rgba(255,255,255,.5)',fontWeight:700,marginBottom:3}}>CARDS DUE</div><div style={{display:'flex',alignItems:'center',gap:5}}><span style={{fontSize:13}}>⏰</span><span style={{fontSize:13,fontWeight:900,color:'#FFB347'}}>{totalDue} due</span></div></div>}
          </div>
        </div>
        {child.iepGoals&&<div style={{background:'#EBF4FB',borderRadius:12,padding:'12px 16px',borderLeft:'4px solid #2E7DB8'}}><div style={{fontSize:10,fontWeight:800,color:'#1A4A6E',textTransform:'uppercase',letterSpacing:.8,marginBottom:5}}>🎯 IEP Goals</div><div style={{fontSize:12,color:'#333',lineHeight:1.7}}>{child.iepGoals}</div></div>}
        <AlgoPanel algData={algData} firstName={child.name.split(' ')[0]}/>
        <div style={{background:'white',borderRadius:14,padding:'18px 20px',border:'1.5px solid #E4ECF5'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:14}}>
            <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:15,fontWeight:800,color:'#1A2E4A'}}>Recent Sessions</div>
            {sessions.length>0&&<Pill label={`${sessions.length} total`} color="#1A4A6E" bg="#EBF4FB"/>}
          </div>
          {sessions.length===0?<EmptyState icon="📋" text="No sessions yet"/>:(
            <div style={{display:'flex',flexDirection:'column',gap:6}}>
              {sessions.slice(0,5).map(s=>{const acc=sessAcc(s),mIcon=MODE_ICON[s.moduleType]||'📌';return(
                <div key={s._id} style={{display:'flex',alignItems:'center',gap:10,background:'#F8FAFD',borderRadius:8,padding:'9px 12px'}}>
                  <span style={{fontSize:16,flexShrink:0}}>{mIcon}</span>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:12,fontWeight:700,color:'#1A2E4A',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.setTitle||s.moduleType}</div>
                    <div style={{fontSize:10,color:'#8FA5BC'}}>{relDate(s.createdAt)}</div>
                  </div>
                  {acc!=null&&<Pill label={`${acc}%`} color={accColor(acc)} bg={accBg(acc)} size={10}/>}
                </div>
              )})}
            </div>
          )}
        </div>
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:14}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8}}>
          {[{icon:'📅',val:sessions.length,label:'Total Sessions',color:'#1A4A6E',bg:'white'},{icon:'🎯',val:avgAcc!=null?`${avgAcc}%`:'—',label:'Avg Accuracy',color:accColor(avgAcc),bg:accBg(avgAcc)},{icon:'✅',val:domainScores.filter(d=>d.pct>=70).length,label:'Domains 70%+',color:'#1B6B45',bg:'#F0FFF4'},{icon:'⏰',val:totalDue>0?totalDue:'✓',label:totalDue>0?'Cards Due':'Caught Up',color:totalDue>0?'#E65100':'#1B6B45',bg:totalDue>0?'#FFF4EC':'#F0FFF4'}].map((s,i)=>(
            <div key={i} style={{background:s.bg,borderRadius:12,padding:'12px 14px',border:'1.5px solid #E4ECF5',textAlign:'center'}}>
              <div style={{fontSize:20,marginBottom:4}}>{s.icon}</div>
              <div style={{fontFamily:"'Playfair Display',serif",fontSize:20,fontWeight:900,color:s.color,lineHeight:1}}>{s.val}</div>
              <div style={{fontSize:9,fontWeight:700,color:'#9E9E9E',marginTop:3}}>{s.label}</div>
            </div>
          ))}
        </div>
        <div style={{background:'white',borderRadius:13,padding:'16px 18px',border:'1.5px solid #E4ECF5'}}>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,color:'#1A2E4A',marginBottom:14}}>NCF Domain Progress</div>
          {domainScores.map(d=>(
            <div key={d.id} style={{marginBottom:11}}>
              <div style={{display:'flex',alignItems:'center',gap:7,marginBottom:4}}>
                <span style={{fontSize:13}}>{d.icon}</span>
                <span style={{flex:1,fontSize:11,fontWeight:600,color:'#3C4F66'}}>{d.short}</span>
                <span style={{fontSize:12,fontWeight:900,color:d.color}}>{d.pct}%</span>
              </div>
              <Bar pct={d.pct} color={d.color} h={5}/>
            </div>
          ))}
        </div>
        <div style={{background:'white',borderRadius:13,padding:'15px 16px',border:'1.5px solid #E4ECF5'}}>
          <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:14,fontWeight:800,color:'#1A2E4A',marginBottom:12}}>Learning Modules</div>
          {[{icon:'🃏',label:'Flashcard Activity',id:'learn',color:'#1A4A6E',desc:'SM-2 spaced repetition'},{icon:'📝',label:'Worksheet Activity',id:'worksheet-learn',color:'#1B8A5A',desc:'Interactive worksheets'},{icon:'🖼️',label:'Activity Cards',id:'activity-learn',color:'#7B2D8B',desc:'Story & find activities'},{icon:'📖',label:'Story Book',id:'storybook-learn',color:'#C0392B',desc:'Read-aloud & retell'}].map((m,i)=>(
            <button key={i} onClick={()=>onNav(m.id)} style={{width:'100%',display:'flex',alignItems:'center',gap:9,padding:'9px 11px',marginBottom:5,background:'#F8FAFD',border:`1px solid #E4ECF5`,borderRadius:9,cursor:'pointer',textAlign:'left'}}
              onMouseEnter={e=>{e.currentTarget.style.background='#EEF3FA';e.currentTarget.style.borderColor=`${m.color}40`}}
              onMouseLeave={e=>{e.currentTarget.style.background='#F8FAFD';e.currentTarget.style.borderColor='#E4ECF5'}}>
              <span style={{fontSize:16,width:24,textAlign:'center',flexShrink:0}}>{m.icon}</span>
              <div style={{flex:1,minWidth:0}}><div style={{fontSize:11.5,fontWeight:700,color:'#3C4F66'}}>{m.label}</div><div style={{fontSize:9,color:'#9EB0C4'}}>{m.desc}</div></div>
              <span style={{fontSize:11,color:'#9EB0C4'}}>→</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ParentDashboard({ onNav }) {
  const { user, getAuthHeader } = useAuth()
  const getHdr = getAuthHeader

  const [children,      setChildren]      = useState([])
  const [records,       setRecords]       = useState({})
  const [sessions,      setSessions]      = useState({})
  const [reviewQueues,  setReviewQueues]  = useState({})
  const [algData,       setAlgData]       = useState({})
  const [algoResults,   setAlgoResults]   = useState({})
  const [assignedSets,  setAssignedSets]  = useState({}) // ← NEW: childId → Flashcard[] | null
  // Work plan state per child
  const [currentPlans,  setCurrentPlans]  = useState({})
  const [futurePlans,   setFuturePlans]   = useState({})
  const [planLoading,   setPlanLoading]   = useState({})
  const [planErrors,    setPlanErrors]    = useState({})
  const [loading,       setLoading]       = useState(true)
  const [activeChild,   setActiveChild]   = useState(null)
  const [activeTab,     setActiveTab]     = useState('overview')

  // ── Load children + records + sessions + queues ──────────────────────────
  useEffect(() => {
    ;(async () => {
      setLoading(true)
      try {
        const res=await fetch(`${API}/children`,{headers:getHdr()})
        const data=await res.json()
        const list=data.children||[]
        setChildren(list); if(list.length) setActiveChild(list[0])
        const [recArr,sessArr,qArr]=await Promise.all([
          Promise.all(list.map(async c=>{try{const r=await fetch(`${API}/progress/${c._id}`,{headers:getHdr()});const d=await r.json();return[c._id,flattenRecord(d.record)]}catch{return[c._id,{}]}})),
          Promise.all(list.map(async c=>{try{const r=await fetch(`${API}/flashcard-sessions/${c._id}?limit=30`,{headers:getHdr()});const d=await r.json();return[c._id,d.sessions||[]]}catch{return[c._id,[]]}})),
          Promise.all(list.map(async c=>{try{const r=await fetch(`${API}/flashcard-sessions/${c._id}/review-queue`,{headers:getHdr()});const d=await r.json();return[c._id,d]}catch{return[c._id,{totalDue:0,queue:[]}]}})),
        ])
        setRecords(Object.fromEntries(recArr))
        setSessions(Object.fromEntries(sessArr))
        setReviewQueues(Object.fromEntries(qArr))
      }catch(e){console.error('ParentDashboard load error',e)}
      finally{setLoading(false)}
    })()
  }, [])

  // ── Fetch algData when child changes — cached ────────────────────────────
  useEffect(() => {
    if(!activeChild) return
    const id=activeChild._id
    if(algData[id]!==undefined) return
    setAlgData(prev=>({...prev,[id]:null}))
    ;(async()=>{
      try{
        const [qRes,sRes,aRes]=await Promise.all([
          fetch(`${API}/flashcard-sessions/${id}/review-queue`,{headers:getHdr()}).then(r=>r.json()).catch(()=>({totalDue:0,queue:[]})),
          fetch(`${API}/flashcard-sessions/${id}?limit=10`,{headers:getHdr()}).then(r=>r.json()).catch(()=>({sessions:[]})),
          fetch(`${API}/flashcard-sessions/${id}/analytics?days=60`,{headers:getHdr()}).then(r=>r.json()).catch(()=>({success:false})),
        ])
        setAlgData(prev=>({...prev,[id]:{queue:qRes,sessions:sRes.sessions||[],analytics:aRes.success?aRes.analytics:null}}))
        fetch(`${API}/algorithm-results/${id}/suggestions?limit=15`,{headers:getHdr()})
          .then(r=>r.json())
          .then(d=>{ if(d.success) setAlgoResults(prev=>({...prev,[id]:d.suggestions||[]})) })
          .catch(()=>{})
      }catch{setAlgData(prev=>({...prev,[id]:{queue:{totalDue:0,queue:[]},sessions:[],analytics:null}}))}
    })()
  }, [activeChild])

  // ── Fetch work plans when child changes — cached ─────────────────────────
  const FUTURE_WEEKS_LOOKAHEAD = 8 // how many weeks ahead to check for prepared plans
  useEffect(() => {
    if(!activeChild) return
    const id=activeChild._id
    if(currentPlans[id]!==undefined) return
    setPlanLoading(prev=>({...prev,[id]:true}))
    setPlanErrors(prev=>({...prev,[id]:""}))
    ;(async()=>{
      try{
        const [curRes,upRes]=await Promise.all([
          fetch(`${API}/workplan/child/${id}/current`,{headers:getHdr()}).then(r=>r.json()).catch(()=>({success:false})),
          fetch(`${API}/workplan/child/${id}/upcoming`,{headers:getHdr()}).then(r=>r.json()).catch(()=>({success:false})),
        ])
        setCurrentPlans(prev=>({...prev,[id]:curRes.success?wpBackendToPlan(curRes.plan):null}))
        setFuturePlans(prev=>({...prev,[id]:upRes.success?(upRes.plans||[]).map(wpBackendToPlan):[]}))
        if(!curRes.success) setPlanErrors(prev=>({...prev,[id]:curRes.message||"No plan found for this week."}))
      }catch{
        setCurrentPlans(prev=>({...prev,[id]:null}))
        setFuturePlans(prev=>({...prev,[id]:[]}))
        setPlanErrors(prev=>({...prev,[id]:"Could not load work plan."}))
      }
      setPlanLoading(prev=>({...prev,[id]:false}))
    })()
  }, [activeChild])

  // ── NEW: Fetch assigned flashcard sets when child changes — cached ────────
  useEffect(() => {
    if(!activeChild) return
    const id = activeChild._id
    if(assignedSets[id] !== undefined) return // already fetched or loading
    setAssignedSets(prev => ({ ...prev, [id]: null })) // null = loading

    fetch(`${API}/flashcard?assignedTo=${id}`, { headers: getHdr() })
      .then(r => r.json())
      .then(d => setAssignedSets(prev => ({ ...prev, [id]: d.flashcards || [] })))
      .catch(() => setAssignedSets(prev => ({ ...prev, [id]: [] })))
  }, [activeChild])

  if(loading) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'80vh',flexDirection:'column',gap:14}}>
      <Spinner size={28}/>
      <div style={{fontSize:13,color:'#9E9E9E'}}>Loading {user?.name?`${user.name}'s`:'your'} dashboard…</div>
      <style>{`@keyframes pd-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )
  if(!children.length) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',height:'80vh',flexDirection:'column',gap:14}}>
      <div style={{fontSize:48}}>👨‍👩‍👧</div>
      <div style={{fontSize:18,fontWeight:800,color:'#1A4A6E'}}>No child profiles found</div>
      <div style={{fontSize:13,color:'#9E9E9E',textAlign:'center',maxWidth:320,lineHeight:1.7}}>Your account doesn't have any children linked yet.<br/>Please contact your school's administrator.</div>
      <style>{`@keyframes pd-spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  )

  const child        = activeChild
  const record       = records[child?._id]||{}
  const sessArr      = sessions[child?._id]||[]
  const reviewQ      = reviewQueues[child?._id]
  const cAlgData     = algData[child?._id]
  const cAlgoResults = algoResults[child?._id]||[]
  const overall      = overallPct(record)
  const totalDue     = reviewQ?.totalDue??0
  const cPlan        = currentPlans[child?._id]
const fPlans       = futurePlans[child?._id] || []
  const isLoading    = planLoading[child?._id]??false
  const pError       = planErrors[child?._id]??""
  const cSets        = assignedSets[child?._id] ?? null // null while loading

  // Badge count: number of assigned sets (only show when loaded and > 0)
  const setsCount = Array.isArray(cSets) ? cSets.length : 0

  const TABS = [
    {id:'overview',   icon:'🏠', label:'Overview'},
    {id:'schedule',   icon:'⏰', label:'SM-2 Schedule', badge:totalDue>0?totalDue:null},
    {id:'workplan',   icon:'📅', label:'Work Plan'},
    {id:'flashcards', icon:'🃏', label:'Flashcard Sets', badge:setsCount>0?setsCount:null}, // ← NEW TAB
    {id:'progress',   icon:'📊', label:'Progress'},
    {id:'sessions',   icon:'🕒', label:`Sessions (${sessArr.length})`},
    {id:'tips',       icon:'💡', label:'Home Tips'},
  ]

  return (
    <div style={{padding:'28px 26px 80px',maxWidth:1220,margin:'0 auto'}}>
      <style>{`@keyframes pd-spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{marginBottom:24}}>
        <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:28,fontWeight:900,color:'#1A2E4A',marginBottom:4,lineHeight:1}}>My Child's Dashboard</div>
        <div style={{fontSize:13,color:'#8FA5BC'}}>Welcome, <strong style={{color:'#3C4F66'}}>{user?.name}</strong> · {new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>
      </div>

      {/* Multi-child selector */}
      {children.length>1&&(
        <div style={{display:'flex',gap:8,marginBottom:20,flexWrap:'wrap'}}>
          {children.map(c=>{
            const sel=activeChild?._id===c._id,op=overallPct(records[c._id]||{})
            return (
              <button key={c._id} onClick={()=>{setActiveChild(c);setActiveTab('overview')}} style={{display:'flex',alignItems:'center',gap:9,padding:'8px 16px',background:sel?'#1A2E4A':'white',color:sel?'white':'#333',border:`1.5px solid ${sel?'#1A2E4A':'#E4EBF2'}`,borderRadius:10,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                <Avatar name={c.name} color={c.color||'#1A4A6E'} size={26} ring={op}/>
                {c.name.split(' ')[0]}
                <Pill label={`${op}%`} color={sel?'rgba(255,255,255,.8)':'#1A4A6E'} bg={sel?'rgba(255,255,255,.15)':'#EBF4FB'} size={10}/>
              </button>
            )
          })}
        </div>
      )}

      {child&&(
        <>
          {/* Stat row */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:24}}>
            {[
              {icon:'📊',val:`${overall}%`,label:'Overall Progress',sub:'NCF all domains',color:'#1A4A6E',bg:'white'},
              {icon:'🏆',val:DOMAINS.filter(d=>domainPct(record,d.id)>=70).length+'/5',label:'Domains on Track',sub:'at 70%+',color:'#1B6B45',bg:'white'},
              {icon:'📅',val:sessArr.length,label:'Total Sessions',sub:'all module types',color:'#7B2D8B',bg:'white'},
              {icon:'🎯',val:(()=>{const r=sessArr.filter(s=>s.ratedCount>0);return r.length?`${Math.round(r.reduce((a,s)=>a+(s.correctCount||0)/s.ratedCount,0)/r.length*100)}%`:'—'})(),label:'Avg Accuracy',sub:'flashcard sessions',color:'#D4AC0D',bg:'white'},
              {icon:'⏰',val:totalDue>0?totalDue:'✓',label:totalDue>0?'Cards Due':'Caught Up',sub:'SM-2 review',color:totalDue>0?'#E65100':'#1B6B45',bg:totalDue>0?'#FFF4EC':'white',onClick:totalDue>0?()=>setActiveTab('schedule'):undefined},
            ].map((s,i)=>(
              <div key={i} onClick={s.onClick} style={{background:s.bg,borderRadius:13,padding:'16px 18px',border:`1.5px solid ${totalDue>0&&i===4?'#FFB347':'#E4ECF5'}`,cursor:s.onClick?'pointer':'default'}}
                onMouseEnter={e=>s.onClick&&(e.currentTarget.style.boxShadow='0 4px 16px rgba(0,0,0,.09)')}
                onMouseLeave={e=>e.currentTarget.style.boxShadow=''}>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
                  <span style={{fontSize:20}}>{s.icon}</span>
                  <span style={{fontSize:9,color:'#8FA5BC',fontWeight:600,background:'#F0F5FC',padding:'2px 7px',borderRadius:8}}>{s.sub}</span>
                </div>
                <div style={{fontFamily:"'Playfair Display',Georgia,serif",fontSize:26,fontWeight:900,color:s.color,lineHeight:1,marginBottom:3}}>{s.val}</div>
                <div style={{fontSize:12,color:'#7A95B2',fontWeight:600}}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div style={{display:'flex',gap:6,marginBottom:22,flexWrap:'wrap'}}>
            {TABS.map(t=>(
              <button key={t.id} onClick={()=>setActiveTab(t.id)} style={{display:'flex',alignItems:'center',gap:7,padding:'8px 16px',background:activeTab===t.id?'#1A2E4A':'white',color:activeTab===t.id?'white':'#3C4F66',border:`1.5px solid ${activeTab===t.id?'#1A2E4A':'#D9E4F0'}`,borderRadius:9,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                <span>{t.icon}</span><span>{t.label}</span>
                {t.badge&&<span style={{background: t.id==='flashcards'?'#1B6B45':'#E65100',color:'white',fontSize:9,fontWeight:900,borderRadius:12,padding:'1px 6px',marginLeft:2}}>{t.badge}</span>}
              </button>
            ))}
          </div>

          {/* Tab content */}
          {activeTab==='overview'   && <OverviewTab child={child} record={record} sessions={sessArr} algData={cAlgData} reviewQueue={reviewQ} onNav={onNav}/>}
          {activeTab==='schedule'   && <ScheduleTab child={child} reviewQueue={reviewQ} sessions={sessArr} onNav={onNav} algoResults={cAlgoResults}/>}
          {activeTab==='workplan'   && <WorkPlanTab child={child} reviewQueue={reviewQ} currentPlan={cPlan} futurePlans={fPlans} planLoading={isLoading} planError={pError} onRefresh={() => {
      setCurrentPlans(prev => { const n={...prev}; delete n[child._id]; return n })
      setFuturePlans(prev => { const n={...prev}; delete n[child._id]; return n })
    }}/>}
          {activeTab==='flashcards' && <FlashcardsTab sets={cSets} child={child} onNav={onNav}/>}
          {activeTab==='progress'   && <ProgressTab record={record} child={child}/>}
          {activeTab==='sessions'   && <SessionsTab sessions={sessArr} child={child}/>}
          {activeTab==='tips'       && <TipsTab record={record}/>}
        </>
      )}
    </div>
  )
}